import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import api from '../api/api';
import { firebaseWebConfig, firebaseWebPushVapidKey } from './firebaseConfig';

const DEVICE_KEY_STORAGE_KEY = 'dashboard_notification_device_key';
const REGISTERED_TOKEN_STORAGE_KEY = 'dashboard_notification_registered_token';

function getOrCreateDeviceKey() {
    const existingKey = localStorage.getItem(DEVICE_KEY_STORAGE_KEY);
    if (existingKey) {
        return existingKey;
    }

    const generatedKey = `dashboard-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_KEY_STORAGE_KEY, generatedKey);
    return generatedKey;
}

function getFirebaseApp() {
    return getApps().length ? getApp() : initializeApp(firebaseWebConfig);
}

async function getMessagingInstance() {
    const supported = await isSupported();
    if (!supported) {
        return null;
    }

    return getMessaging(getFirebaseApp());
}

export function isFirebaseWebPushConfigured() {
    return Boolean(firebaseWebPushVapidKey);
}

export async function registerDashboardPushToken() {
    if (!isFirebaseWebPushConfigured()) {
        return { configured: false, reason: 'missing-vapid-key' };
    }

    if (!('serviceWorker' in navigator) || !('Notification' in window)) {
        return { configured: false, reason: 'unsupported-browser' };
    }

    const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();

    if (permission !== 'granted') {
        return { configured: false, reason: 'permission-denied' };
    }

    const messaging = await getMessagingInstance();
    if (!messaging) {
        return { configured: false, reason: 'unsupported-messaging' };
    }

    const serviceWorkerRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    const token = await getToken(messaging, {
        vapidKey: firebaseWebPushVapidKey,
        serviceWorkerRegistration,
    });

    if (!token) {
        return { configured: false, reason: 'token-unavailable' };
    }

    await api.post('/notifications/devices/register', {
        token,
        provider: 'fcm',
        platform: 'web',
        deviceKey: getOrCreateDeviceKey(),
        deviceName: navigator.userAgent.slice(0, 120),
    });

    localStorage.setItem(REGISTERED_TOKEN_STORAGE_KEY, token);

    return { configured: true, token };
}

export async function unregisterDashboardPushToken() {
    const token = localStorage.getItem(REGISTERED_TOKEN_STORAGE_KEY);
    const deviceKey = localStorage.getItem(DEVICE_KEY_STORAGE_KEY);

    if (!token && !deviceKey) {
        return;
    }

    try {
        await api.post('/notifications/devices/unregister', {
            token: token || undefined,
            deviceKey: deviceKey || undefined,
        });
    } catch (error) {
        console.error('Failed to unregister dashboard push token:', error);
    } finally {
        localStorage.removeItem(REGISTERED_TOKEN_STORAGE_KEY);
    }
}

export async function subscribeToForegroundMessages(onPayload) {
    const messaging = await getMessagingInstance();
    if (!messaging) {
        return () => {};
    }

    return onMessage(messaging, onPayload);
}
